import { t as uiText } from '../i18n';
/**
 * 運営側スーパー管理画面のパスヘルパー。
 *
 * 2026-04 以降、運営管理コンソールは **専用 Hosting site (`admin-notreu` /
 * `admin-notreu-stg`) のドメインルート直下** にフラット展開される
 * （旧 `/sys/admin/*` プレフィックスは廃止）。
 *
 * - 本番: `https://admin.notreu.com/dashboard` 等
 * - Staging: `https://admin-notreu-stg.web.app/dashboard` 等
 * - 開発時: `http://admin.localhost:3000/dashboard` 等
 *
 * 一方、メインアプリ側ドメイン (`notreu.com` / `localhost` 等) では
 * これらのパスはマウントされず NotFound に倒れる（`isAdminHost()` ガード）。
 */

export const serviceAdminPaths = {
  root: '/',
  dashboard: '/dashboard',
  workspaces: '/workspaces',
  workspaceDetail: (workspaceId: string) =>
    `/workspaces/${encodeURIComponent(workspaceId)}`,
  companies: '/companies',
  companyDetail: (companyId: string) =>
    `/companies/${encodeURIComponent(companyId)}`,
  users: '/users',
  errors: '/errors',
  feedback: '/feedback',
  operators: '/operators',
  offerCatalog: '/offer-catalog',
  campaigns: '/campaigns',
  slack: '/slack',
};

export const SERVICE_ADMIN_NAV_ITEMS = [
  { key: 'dashboard', get label() { return uiText("Dashboard"); }, path: serviceAdminPaths.dashboard },
  { key: 'workspaces', get label() { return uiText("Workspace"); }, path: serviceAdminPaths.workspaces },
  { key: 'companies', get label() { return uiText("Organization"); }, path: serviceAdminPaths.companies },
  { key: 'users', get label() { return uiText("User"); }, path: serviceAdminPaths.users },
  { key: 'errors', get label() { return uiText("Error analysis"); }, path: serviceAdminPaths.errors },
  { key: 'feedback', get label() { return uiText("Feedback"); }, path: serviceAdminPaths.feedback },
  { key: 'operators', get label() { return uiText("Operations members"); }, path: serviceAdminPaths.operators },
  { key: 'offerCatalog', get label() { return uiText("Offer settings"); }, path: serviceAdminPaths.offerCatalog },
  { key: 'campaigns', get label() { return uiText("Campaigns"); }, path: serviceAdminPaths.campaigns },
  { key: 'slack', get label() { return uiText("Slack notifications"); }, path: serviceAdminPaths.slack },
] as const;

export type ServiceAdminNavKey = (typeof SERVICE_ADMIN_NAV_ITEMS)[number]['key'];

/**
 * 運営管理コンソールを配信する Hosting site のホスト名一覧。
 * これら以外のホスト（メイン site / カスタムドメイン / 素の localhost）からは
 * 運営ルート（`/dashboard`, `/workspaces`, `/companies`, `/users`, `/errors`,
 * `/operators`）をマウントせず、SPA の NotFound に倒して運営URLを露出させない。
 *
 * NOTE:
 * - admin-notreu / admin-notreu-stg にカスタムドメインを接続した場合は
 *   その FQDN もここに追記すること。
 * - 開発時は `http://admin.localhost:3000` でアクセスする（`*.localhost` は
 *   ブラウザが自動的にループバックに解決する RFC 6761 の仕様を利用）。
 *   素の `http://localhost:3000` はメインアプリ側として扱う。
 */
export const ADMIN_HOSTS: ReadonlyArray<string> = [
  'admin.notreu.com',
  'admin-notreu.web.app',
  'admin-notreu.firebaseapp.com',
  'admin-notreu-stg.web.app',
  'admin-notreu-stg.firebaseapp.com',
  'admin.localhost',
];

export function isAdminHost(): boolean {
  if (typeof window === 'undefined') return true;
  const host = window.location.hostname;
  return ADMIN_HOSTS.includes(host);
}
